import Api from '@/misc/api'
import type { Commit, ListMcoStaySuggestionsResponse, ListSmrStaySuggestionsResponse, McoStaySuggestionItem, PreviouslyDisplayedMcoStay, PreviouslyDisplayedSmrStay, SmrStaySuggestionItem } from '@/misc/types'
import type { RawMcoStay } from '@/store/modules/stay/mco/types'
import type { RawSmrStay } from '@/store/modules/stay/smr/types'

// Routed through Api.get (instead of a bare fetch) so a 401 triggers the same session-expiry
// retry, and a failure surfaces the same error toast, as every other API call in the app.
function fetchMcoStaySuggestions(url: string, commit: Commit): Promise<ListMcoStaySuggestionsResponse> {
  return new Promise((resolve, reject) => {
    Api.get(
      {
        url,
        successMut: (data: ListMcoStaySuggestionsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch stay suggestions (${url})`)),
      },
      commit
    )
  })
}

function fetchSmrStaySuggestions(url: string, commit: Commit): Promise<ListSmrStaySuggestionsResponse> {
  return new Promise((resolve, reject) => {
    Api.get(
      {
        url,
        successMut: (data: ListSmrStaySuggestionsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch stay suggestions (${url})`)),
      },
      commit
    )
  })
}

/**
 * Fetched in parallel with the stay/documents/justifications requests; the promise is threaded
 * into refreshDisplayed*Stay as a fallback when no previously displayed stay can be reused.
 * includeBelowThreshold mirrors prefetchMcoStayJustifications/prefetchMcoStayDocuments: the page
 * séjour passes true (frontend applies its own display threshold), the staylist quick view passes
 * false. onlyNonCodedDas: only the staylist quick view passes true (see tableau-de-bord-listes-travail.md).
 */
export function prefetchMcoStaySuggestions(idType: string, stayId: string, commit: Commit, onlyNonCodedDas: boolean, includeBelowThreshold: boolean): Promise<ListMcoStaySuggestionsResponse> {
  return fetchMcoStaySuggestions(`/api/stay/${stayId}/suggestions?idType=${idType}&includeBelowThreshold=${includeBelowThreshold ? 1 : 0}&onlyNonCodedDas=${onlyNonCodedDas ? 1 : 0}`, commit)
}

export function prefetchSmrStaySuggestions(idType: string, stayId: string, commit: Commit): Promise<ListSmrStaySuggestionsResponse> {
  return fetchSmrStaySuggestions(`/api/smr/stay/${stayId}/suggestions?idType=${idType}`, commit)
}

/**
 * The suggestions list does not depend on the coding state of the stay (it's derived from the
 * predicted labels and saved searches, unaffected by adding/removing a diagnosis), so like the
 * documents/justifications it is reused as-is rather than refetched on every mutation.
 */
export async function getStaySuggestions(
  stay: RawMcoStay,
  commit: Commit,
  previousStay: PreviouslyDisplayedMcoStay | null,
  prefetched: Promise<ListMcoStaySuggestionsResponse> | null,
  includeBelowThreshold: boolean
): Promise<McoStaySuggestionItem[]> {
  if (previousStay?.id === stay.id) {
    return previousStay.staySuggestions ?? []
  }

  try {
    const response = await (prefetched ?? fetchMcoStaySuggestions(`/api/stay/${stay.id}/suggestions?idType=id&includeBelowThreshold=${includeBelowThreshold ? 1 : 0}`, commit))

    return response.sancareSuggestions
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)

    return []
  }
}

export async function getSmrStaySuggestions(
  stay: RawSmrStay,
  commit: Commit,
  previousStay: PreviouslyDisplayedSmrStay | null,
  prefetched: Promise<ListSmrStaySuggestionsResponse> | null
): Promise<SmrStaySuggestionItem[]> {
  if (previousStay?.id === stay.id) {
    return previousStay.staySuggestions ?? []
  }

  try {
    const response = await (prefetched ?? fetchSmrStaySuggestions(`/api/smr/stay/${stay.id}/suggestions?idType=id`, commit))

    return response.sancareSuggestions
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)

    return []
  }
}
